import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { format, startOfDay, endOfDay, addDays } from 'date-fns';
import {
  Loader2, Eye, Heart, MessageCircle, Share2, ExternalLink, RefreshCw,
  Film, PoundSterling, Pencil, AlertTriangle, TrendingUp, Lock, FileText, Check, X,
  Play, ShoppingBag,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import SEO from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { ProductsInVideos, ShopBadge, ViewProductLink, proxyImg } from '@/components/VideoProducts';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('kylo_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};
const toMillis = (ct) => {
  if (!ct) return 0;
  if (/^\d+$/.test(String(ct))) {
    const n = parseInt(ct, 10);
    return n < 1e12 ? n * 1000 : n;
  }
  const t = Date.parse(ct);
  return Number.isNaN(t) ? 0 : t;
};
const fmtDate = (ct) => {
  const ms = toMillis(ct);
  return ms ? new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
};

const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#FFFDF9', border: '1px solid #E6DED1', borderRadius: 10,
  color: '#1F1B16', fontSize: 12, boxShadow: '0 8px 24px rgba(70,45,20,0.10)',
};

const PRESETS = [
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
];

const TRENDING_PERIODS = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days', pro: true },
];

// ---- Warm-themed chart card -------------------------------------------------
const ChartCard = ({ title, data, dataKey, color, overlayKey, overlayColor, overlayName, footer }) => (
  <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4 overflow-hidden">
    <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-3">{title}</p>
    <div className="h-52 sm:h-48 w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: overlayKey ? 2 : 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EAE0D2" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" interval="preserveStartEnd" minTickGap={24} />
          <YAxis yAxisId="left" tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" tickFormatter={fmt} width={40} allowDecimals={false} />
          {overlayKey && (
            <YAxis yAxisId="right" orientation="right" tick={{ fill: overlayColor, fontSize: 10 }} stroke="#D9CFC0" tickFormatter={(v) => `£${fmt(v)}`} width={30} mirror />
          )}
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={{ color: '#6F675D' }}
            formatter={(val, name, props) => {
              if (overlayName && name === overlayName) {
                const comm = props?.payload?.commission;
                const commStr = (comm || comm === 0) ? ` · £${Number(comm).toLocaleString('en-GB')} earned` : '';
                return [`£${Number(val).toLocaleString('en-GB')}${commStr}`, overlayName];
              }
              return [Number(val).toLocaleString('en-GB'), title];
            }}
          />
          <Line yAxisId="left" name={title} type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 2, fill: color }} activeDot={{ r: 4 }} />
          {overlayKey && (
            <Line yAxisId="right" name={overlayName} type="monotone" dataKey={overlayKey} stroke={overlayColor} strokeWidth={2.5} dot={{ r: 3, fill: overlayColor }} activeDot={{ r: 5 }} connectNulls={false} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
    {footer}
  </div>
);

// ---- Handle onboarding / edit dialog ---------------------------------------
const HandleDialog = ({ open, onOpenChange, isFirstTime, initial, onSaved }) => {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setValue(initial || ''); }, [open, initial]);

  const save = async () => {
    const h = value.trim().replace(/^@/, '');
    if (!h) { toast.error('Enter your TikTok handle'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/me/tiktok-handle`, { handle: h }, { headers: authHeaders() });
      toast.success('TikTok handle saved');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save handle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFDF9] border-[#E6DED1] sm:max-w-md" data-testid="handle-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#1F1B16]">
            {isFirstTime ? 'Paste your TikTok profile' : 'Change your TikTok profile'}
          </DialogTitle>
          <DialogDescription className="text-[#6F675D]">
            This connects your dashboard to your TikTok profile.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2.5 rounded-[12px] bg-[#FDF2D8] border border-[#E6D199] p-3 mb-1">
          <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-xs text-[#7A5A1E] leading-snug" data-testid="handle-warning">
            Please double-check for typos. You can set this once, then it can only be changed
            <strong> once every 30 days</strong>.
          </p>
        </div>
        <div>
          <Label className="text-xs text-[#6F675D]">TikTok handle</Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9D8E7B]">@</span>
            <Input
              value={value.replace(/^@/, '')}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              placeholder="yourhandle"
              className="pl-7 bg-[#FBF7F0] border-[#E6DED1] text-[#1F1B16]"
              data-testid="handle-input"
            />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="w-full mt-2 bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24]" data-testid="handle-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {isFirstTime ? 'Save handle' : 'Save change'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.8} />
      <p className="text-[11px] uppercase tracking-wider text-[#8A8178]">{label}</p>
    </div>
    <p className="text-2xl font-display text-[#1F1B16]">{fmt(value)}</p>
  </div>
);

// ---- Top 10 Products from this week (live product data) --------------------
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const pct = (n) => (n == null ? null : `${Math.round(Number(n) * 10) / 10}%`);

const ProductVideos = ({ productId, period, productCover, onPulled }) => {
  const [videos, setVideos] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [txId, setTxId] = useState(null);

  useEffect(() => {
    let active = true;
    setVideos(null);
    setLimitReached(false);
    axios.get(`${API}/api/me/trending/product-videos`, { params: { product_id: productId, period }, headers: authHeaders() })
      .then((r) => {
        if (!active) return;
        setVideos(r.data?.videos || []);
        if (typeof r.data?.video_pulls_remaining === 'number' && onPulled) onPulled(r.data.video_pulls_remaining);
      })
      .catch((err) => {
        if (!active) return;
        if (err.response?.status === 429) { setLimitReached(true); setVideos([]); }
        else setVideos([]);
      });
    return () => { active = false; };
  }, [productId, period]);

  const downloadTranscript = async (v) => {
    setTxId(v.video_id);
    try {
      const r = await axios.post(`${API}/api/me/transcribe`, { url: v.url, video_id: v.video_id }, { headers: authHeaders(), timeout: 60000 });
      const text = r.data?.transcript || '';
      if (!text.trim()) { toast.error('No transcript available for this video'); return; }
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `transcript-${v.creator_handle || v.video_id}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('Transcript downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transcription failed');
    } finally { setTxId(null); }
  };

  if (videos === null) return <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-[#9D174D]" /></div>;
  if (limitReached) return (
    <div className="rounded-[12px] border border-[#F3D7A6] bg-[#FDF6E7] px-3 py-3 flex items-start gap-2" data-testid="product-videos-limit">
      <Lock className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={2} />
      <p className="text-xs text-[#8A5A14] leading-relaxed">You've used your 2 product video lookups for today. Come back tomorrow to explore more products' videos.</p>
    </div>
  );
  if (videos.length === 0) return <p className="text-xs text-[#9D8E7B] py-2" data-testid="product-videos-empty">No shoppable videos found for this product.</p>;

  return (
    <div className="space-y-2" data-testid="product-videos-list">
      {videos.map((v, i) => {
        const thumb = proxyImg(v.cover || productCover);
        return (
          <div key={v.video_id || i} className="rounded-[12px] border border-[#EFE1D1] bg-[#FBF7F0] overflow-hidden" data-testid={`product-video-${i}`}>
            <a href={v.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 hover:bg-[#F0E8DB] transition-colors">
              <div className="relative w-11 h-14 rounded-md overflow-hidden bg-[#2B2740] flex items-center justify-center shrink-0">
                {thumb ? <img src={thumb} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" /> : null}
                <span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play className="w-4 h-4 text-white fill-white" /></span>
                {v.is_ad && (
                  <span className="absolute top-0.5 left-0.5 bg-[#9D174D] text-white text-[8px] font-bold rounded px-1 shadow" data-testid={`product-video-ad-${i}`}>AD</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#1F1B16] font-medium truncate">@{v.creator_handle || 'creator'}</p>
                <p className="text-[11px] text-[#6F675D] inline-flex items-center gap-2 mt-0.5">
                  <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3 text-[#B45309]" />{fmt(v.views)}</span>
                  <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3 text-[#9D174D]" />{fmt(v.likes)}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[9px] uppercase tracking-wider text-[#8A8178] leading-none mb-0.5">EST</p>
                <p className="text-sm font-display text-[#1E7A6B] leading-none">{money(v.revenue)}</p>
              </div>
            </a>
            <button
              type="button"
              onClick={() => downloadTranscript(v)}
              disabled={txId === v.video_id}
              className="w-full border-t border-[#EFE1D1] py-1.5 text-[11px] font-semibold text-[#9D174D] hover:bg-[#FBE4EE] disabled:opacity-60 inline-flex items-center justify-center gap-1 transition-colors"
              data-testid={`product-video-transcript-${i}`}
            >
              {txId === v.video_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              {txId === v.video_id ? 'Transcribing…' : 'Download transcript'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const TrendingBlock = () => {
  const { isPro } = useAuth();
  const [period, setPeriod] = useState('7d');
  const [category, setCategory] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [unlocked, setUnlocked] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/api/me/trending`, { params: { period, category }, headers: authHeaders() })
      .then((r) => {
        if (!active) return;
        setData(r.data);
        if (typeof r.data?.video_pulls_remaining === 'number') setRemaining(r.data.video_pulls_remaining);
        setUnlocked(r.data?.unlocked_product_ids || []);
      })
      .catch(() => { if (active) toast.error('Could not load top products'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period, category]);

  const openProduct = (p) => {
    const pid = String(p.product_id || '');
    if (remaining !== null && remaining <= 0 && !unlocked.includes(pid)) {
      toast.info("You've used your 2 product video lookups for today. Come back tomorrow for more.");
      return;
    }
    setSelected(p);
  };

  const handlePulled = (rem) => {
    if (typeof rem === 'number') setRemaining(rem);
    if (selected?.product_id) setUnlocked((u) => (u.includes(String(selected.product_id)) ? u : [...u, String(selected.product_id)]));
  };

  const addToProducts = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/api/folders/from-trending`, {
        product_name: selected.product_name,
        url: selected.url,
        product_id: selected.product_id,
        unit_price: selected.unit_price,
        cover: selected.cover,
      }, { headers: authHeaders() });
      toast.success(r.data?.already_existed ? 'Already in your Products' : 'Added to your Products');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add to Products');
    } finally { setAdding(false); }
  };

  const categories = data?.categories || [];
  const products = data?.products || [];

  return (
    <div className="mt-10" data-testid="trending-section">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingBag className="w-4 h-4 text-[#9D174D]" strokeWidth={1.8} />
        <h3 className="font-display text-base text-[#1F1B16]">Top 10 Products from this week…</h3>
      </div>
      <p className="text-xs text-[#9D8E7B] mb-3">The best-selling TikTok Shop products (UK), ranked by revenue. Tap a product for its top videos and link.</p>

      {remaining !== null && (
        <div className="inline-flex items-center gap-1.5 mb-3 rounded-full bg-[#FDF6E7] border border-[#F3D7A6] px-3 py-1" data-testid="trending-pulls-remaining">
          <Play className="w-3 h-3 text-[#B45309]" />
          <span className="text-[11px] text-[#8A5A14]">
            {remaining > 0
              ? <><span className="font-semibold">{remaining}</span> product video {remaining === 1 ? 'lookup' : 'lookups'} left today</>
              : 'Daily video lookups used — resets tomorrow'}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-2" data-testid="trending-period">
          {TRENDING_PERIODS.map((p) => {
            const locked = p.pro && !isPro;
            return (
              <button
                key={p.id}
                onClick={() => locked
                  ? toast.info('Last 30 days is a Pro feature. Upgrade to unlock 30-day product data.')
                  : setPeriod(p.id)}
                className={`h-9 px-4 rounded-full text-sm transition-colors border inline-flex items-center gap-1.5 ${
                  period === p.id
                    ? 'bg-[#1F1B16] text-[#FFFDF9] border-[#1F1B16]'
                    : locked
                      ? 'bg-[#FBF7F0] text-[#B0A89B] border-[#E6DED1] hover:bg-[#F0E8DB]'
                      : 'bg-[#FBF7F0] text-[#5E574E] border-[#E6DED1] hover:bg-[#F0E8DB]'
                }`}
                data-testid={`trending-period-${p.id}`}
                title={locked ? 'Pro plan required for 30-day data' : undefined}
              >
                {locked && <Lock className="w-3.5 h-3.5" strokeWidth={2} />}
                {p.label}
              </button>
            );
          })}
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 px-3 rounded-full text-sm bg-[#FBF7F0] text-[#5E574E] border border-[#E6DED1] focus:outline-none focus:border-[#9D174D] cursor-pointer"
          data-testid="trending-category"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#9D174D]" /></div>
      ) : products.length === 0 ? (
        <p className="text-sm text-[#6F675D]" data-testid="trending-empty">No products found for this filter.</p>
      ) : (
        <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] divide-y divide-[#EFE1D1] overflow-hidden" data-testid="trending-list">
          {products.map((p) => (
            <button
              key={p.product_id}
              type="button"
              onClick={() => openProduct(p)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F0E8DB] transition-colors"
              data-testid={`trending-row-${p.rank}`}
            >
              <span className="w-5 text-center font-display text-sm text-[#9D174D] shrink-0">{p.rank}</span>
              <div className="w-12 h-12 rounded-md overflow-hidden bg-[#F0E8DB] shrink-0">
                {p.cover ? (
                  <img src={p.cover} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-4 h-4 text-[#C9BCA8]" /></div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#1F1B16] truncate font-medium">{p.product_name}</p>
                <p className="text-xs text-[#9D8E7B] truncate">
                  {fmt(p.units_sold)} sold{p.commission_rate != null ? <> · <span className="text-[#A3762F]">{pct(p.commission_rate)} commission</span></> : null}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[9px] uppercase tracking-wider text-[#8A8178] leading-none">EST</p>
                <p className="text-sm font-display text-[#1E7A6B]">{money(p.revenue)}</p>
                <p className="text-[11px] text-[#6F675D]">revenue</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Product detail popup */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="bg-[#FFFDF9] border-[#E6DED1] sm:max-w-lg" data-testid="product-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-[#1F1B16] text-left pr-6">{selected.product_name}</DialogTitle>
                <DialogDescription className="text-[#6F675D] text-left">Top TikTok Shop product this period.</DialogDescription>
              </DialogHeader>

              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-[12px] overflow-hidden bg-[#F0E8DB] shrink-0">
                  {selected.cover ? <img src={selected.cover} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-[#C9BCA8]" /></div>}
                </div>
                <div className="grid grid-cols-3 gap-2 flex-1">
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-[#B45309] leading-none mb-0.5">EST</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#8A8178]">Revenue</p>
                    <p className="text-base font-display text-[#1E7A6B]">{money(selected.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] leading-none mb-0.5">&nbsp;</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#8A8178]">Units</p>
                    <p className="text-base font-display text-[#1F1B16]">{fmt(selected.units_sold)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] leading-none mb-0.5">&nbsp;</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#8A8178]">Commission</p>
                    <p className="text-base font-display text-[#1F1B16]">{selected.commission_rate != null ? pct(selected.commission_rate) : '—'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {selected.url && (
                  <a href={selected.url} target="_blank" rel="noopener noreferrer" data-testid="product-tiktok-link">
                    <Button className="w-full bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24]">
                      <ExternalLink className="w-4 h-4 mr-2" /> View on TikTok
                    </Button>
                  </a>
                )}
                <Button
                  onClick={addToProducts}
                  disabled={adding}
                  className="w-full bg-[#9D174D] text-white hover:bg-[#82113f]"
                  data-testid="add-to-products-btn"
                >
                  {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
                  Add to my products
                </Button>
              </div>

              <div>
                <h4 className="text-sm font-display text-[#1F1B16] mb-2 flex items-center gap-1.5"><Play className="w-3.5 h-3.5 text-[#9D174D]" /> Top videos</h4>
                <ProductVideos productId={selected.product_id} period={period} productCover={selected.cover} onPulled={handlePulled} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DemoStats = ({ onAdd }) => (
  <div className="relative mt-8" data-testid="demo-stats">
    <div className="blur-[3px] pointer-events-none select-none" aria-hidden="true">
      <div className="flex flex-wrap gap-2 mb-6">
        {['Last 7 days', 'Last 14 days', 'Last 30 days'].map((l, i) => (
          <span key={l} className={`h-9 px-4 inline-flex items-center rounded-full text-sm border ${i === 0 ? 'bg-[#1F1B16] text-[#FFFDF9] border-[#1F1B16]' : 'bg-[#FBF7F0] text-[#5E574E] border-[#E6DED1]'}`}>{l}</span>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Film} label="Videos posted" value={42} accent="text-[#9D174D]" />
        <StatCard icon={Eye} label="Total views" value={1200000} accent="text-[#B45309]" />
        <StatCard icon={Heart} label="Total likes" value={98000} accent="text-[#9D174D]" />
        <StatCard icon={MessageCircle} label="Total comments" value={4300} accent="text-[#A3762F]" />
        <StatCard icon={Share2} label="Total shares" value={2100} accent="text-[#1E7A6B]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-[16px] border border-[#D7EDE7] bg-[#EEF7F4] p-4"><p className="text-[11px] uppercase tracking-wider text-[#5E8F83] mb-1">Total GMV</p><p className="text-2xl font-display text-[#1E7A6B]">£8,420</p></div>
        <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4"><p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">Commission earned</p><p className="text-2xl font-display text-[#1F1B16]">£1,180</p></div>
        <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4"><p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">GMV per 1K views</p><p className="text-2xl font-display text-[#1F1B16]">£7.02</p></div>
      </div>
      <h3 className="font-display text-base text-[#1F1B16] mb-3">Recent videos</h3>
      <div className="grid grid-cols-3 lg:grid-cols-8 gap-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-[10px] border border-[#EFE1D1] bg-[#FBF7F0] overflow-hidden">
            <div className="w-full aspect-[9/16] bg-gradient-to-br from-[#F0E8DB] to-[#E7D9C6] flex items-center justify-center"><Film className="w-4 h-4 text-[#C9BCA8]" /></div>
            <div className="px-2 py-1.5">
              <div className="h-2 w-10 bg-[#ECE1D2] rounded mb-1.5" />
              <div className="flex justify-between"><span className="h-2 w-6 bg-[#ECE1D2] rounded inline-block" /><span className="h-2 w-6 bg-[#ECE1D2] rounded inline-block" /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute inset-0 flex items-start justify-center px-4 pt-10">
      <div className="max-w-md w-full rounded-[18px] border border-[#E6DED1] bg-[#FFFDF9]/95 shadow-xl p-6 text-center backdrop-blur-sm" data-testid="connect-overlay">
        <div className="w-12 h-12 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center mb-4 mx-auto">
          <TrendingUp className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <h2 className="font-display text-xl text-[#1F1B16] mb-2">Paste your TikTok profile</h2>
        <p className="text-[#6F675D] text-sm mb-4 leading-relaxed">
          Paste your TikTok profile to replace this sample with your real videos, views, engagement and daily GMV.
        </p>
        <div className="flex items-start gap-2.5 rounded-[12px] bg-[#FDF2D8] border border-[#E6D199] p-3 mb-4 text-left">
          <Lock className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-xs text-[#7A5A1E] leading-snug">
            You get <strong>one chance</strong> to add your handle — after that you can change it <strong>once every 30 days</strong>. Double-check it before saving.
          </p>
        </div>
        <Button onClick={onAdd} className="bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24] h-11 px-6 w-full" data-testid="add-handle-btn">
          Paste your TikTok profile
        </Button>
      </div>
    </div>
  </div>
);

const PreparingView = ({ immediate, fetching, onCheck }) => {
  const [showLong, setShowLong] = useState(!!immediate);
  useEffect(() => {
    if (immediate) { setShowLong(true); return undefined; }
    const t = setTimeout(() => setShowLong(true), 3000);
    return () => clearTimeout(t);
  }, [immediate]);
  return (
    <AppShell title="Dashboard">
      <div className="flex flex-col items-center justify-center text-center h-[62vh] px-6">
        <Loader2 className="w-6 h-6 animate-spin text-[#9D174D] mb-4" />
        {showLong ? (
          <>
            <h2 className="font-display text-xl text-[#1F1B16] mb-2" data-testid="preparing-title">We're pulling your TikTok data</h2>
            <p className="text-sm text-[#6F675D] max-w-sm leading-relaxed mb-5">
              This can take a few minutes. Feel free to leave this page and <strong>check back in a few minutes</strong> — we'll have your videos and stats ready.
            </p>
            <Button onClick={onCheck} disabled={fetching} variant="outline" className="border-[#E6DED1] bg-[#FBF7F0] text-[#1F1B16] hover:bg-[#F0E8DB]" data-testid="preparing-check-btn">
              {fetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Check now
            </Button>
          </>
        ) : (
          <p className="text-sm text-[#6F675D]">Loading your dashboard…</p>
        )}
      </div>
    </AppShell>
  );
};

const CreatorDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [activeDays, setActiveDays] = useState(7);
  const [handleOpen, setHandleOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [adsOnly, setAdsOnly] = useState(false);
  const gmvChartRef = useRef(null);
  const [gmvHint, setGmvHint] = useState(false);

  const nudgeGmv = () => {
    setGmvHint(true);
    if (gmvChartRef.current) gmvChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => setGmvHint(false), 4500);
  };

  // GMV logger (persisted per user, keyed by date)
  const [gmvByDay, setGmvByDay] = useState({});
  const [gmvOpen, setGmvOpen] = useState(false);
  const [gmvForm, setGmvForm] = useState({ day: '', gmv: '', commission: '' });
  // Per-video transcripts + "About you" inline editing
  const [transcripts, setTranscripts] = useState({});
  const [transcribingId, setTranscribingId] = useState(null);
  const [transcriptView, setTranscriptView] = useState(null); // { label, text }
  const [editingAbout, setEditingAbout] = useState(null);
  const [aboutDraft, setAboutDraft] = useState('');

  const loadDashboard = useCallback(async () => {
    setFetching(true);
    try {
      const r = await axios.get(`${API}/api/me/creator-dashboard`, { headers: authHeaders(), timeout: 120000 });
      setData(r.data);
      if (r.data?.videos?.length || r.data?.needs_handle) setJustAdded(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your dashboard');
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  const loadGmv = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/me/gmv`, { headers: authHeaders() });
      setGmvByDay(r.data?.entries || {});
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { loadDashboard(); loadGmv(); }, [loadDashboard, loadGmv]);

  const onHandleSaved = useCallback(() => {
    setData(null);
    setJustAdded(true);
    loadDashboard();
  }, [loadDashboard]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/api/me/creator-dashboard/refresh`, {}, { headers: authHeaders(), timeout: 120000 });
      setData(r.data);
      toast.success('Refreshed with the latest videos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const dateRange = useMemo(() => {
    const today = new Date();
    return { from: startOfDay(addDays(today, -(activeDays - 1))), to: endOfDay(today) };
  }, [activeDays]);

  const videos = useMemo(() => {
    const all = (data?.videos || []).slice();
    const from = dateRange.from.getTime();
    const to = dateRange.to.getTime();
    return all
      .filter((v) => { const t = toMillis(v.create_time); return t >= from && t <= to; })
      .sort((a, b) => toMillis(b.create_time) - toMillis(a.create_time));
  }, [data, dateRange]);

  const totals = useMemo(() => videos.reduce(
    (a, v) => ({
      views: a.views + (v.views || 0),
      likes: a.likes + (v.likes || 0),
      comments: a.comments + (v.comments || 0),
      shares: a.shares + (v.shares || 0),
    }),
    { views: 0, likes: 0, comments: 0, shares: 0 },
  ), [videos]);

  const adCount = useMemo(() => videos.filter((v) => v.is_ad).length, [videos]);
  const displayVideos = useMemo(() => (adsOnly ? videos.filter((v) => v.is_ad) : videos), [videos, adsOnly]);

  const daily = useMemo(() => {
    const buckets = {};
    videos.forEach((v) => {
      const key = format(new Date(toMillis(v.create_time)), 'yyyy-MM-dd');
      if (!buckets[key]) buckets[key] = { videos: 0, views: 0 };
      buckets[key].videos += 1;
      buckets[key].views += v.views || 0;
    });
    const out = [];
    for (let i = 0; i < activeDays; i += 1) {
      const d = addDays(dateRange.from, i);
      const key = format(d, 'yyyy-MM-dd');
      const b = buckets[key] || { videos: 0, views: 0 };
      out.push({ key, label: format(d, 'd MMM'), ...b });
    }
    return out;
  }, [videos, dateRange, activeDays]);

  const dailyWithGmv = useMemo(
    () => daily.map((d) => ({ ...d, gmv: gmvByDay[d.key]?.gmv ?? null, commission: gmvByDay[d.key]?.commission ?? null })),
    [daily, gmvByDay],
  );
  const dayLabel = useCallback((key) => daily.find((d) => d.key === key)?.label || key, [daily]);

  // GMV / earnings summary for the selected range.
  const gmvSummary = useMemo(() => {
    let gmv = 0; let commission = 0; let any = false;
    daily.forEach((d) => {
      const e = gmvByDay[d.key];
      if (e) { any = true; gmv += e.gmv || 0; commission += e.commission || 0; }
    });
    const per1k = totals.views > 0 ? gmv / (totals.views / 1000) : 0;
    return { gmv, commission, per1k, any };
  }, [daily, gmvByDay, totals.views]);

  const openGmvForDay = (key) => {
    const existing = gmvByDay[key];
    setGmvForm({ day: key, gmv: existing?.gmv != null ? String(existing.gmv) : '', commission: existing?.commission != null ? String(existing.commission) : '' });
    setGmvOpen(true);
  };
  const saveGmvEntry = async () => {
    const gmv = parseFloat(gmvForm.gmv);
    if (Number.isNaN(gmv)) { toast.error('Enter a GMV total'); return; }
    const commission = gmvForm.commission === '' ? null : parseFloat(gmvForm.commission);
    try {
      await axios.put(`${API}/api/me/gmv`, { date: gmvForm.day, gmv, commission }, { headers: authHeaders() });
      setGmvByDay((p) => ({ ...p, [gmvForm.day]: { gmv, commission } }));
      toast.success(`Saved £${gmv.toLocaleString('en-GB')} for ${dayLabel(gmvForm.day)}`);
      setGmvOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save GMV');
    }
  };
  const removeGmvEntry = async (key) => {
    try {
      await axios.delete(`${API}/api/me/gmv/${key}`, { headers: authHeaders() });
      setGmvByDay((p) => { const n = { ...p }; delete n[key]; return n; });
      setGmvOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove GMV');
    }
  };

  const canRefresh = !data?.can_refresh_at || new Date(data.can_refresh_at) <= new Date();
  const creator = data?.creator;

  const startEditAbout = (key, current) => { setEditingAbout(key); setAboutDraft(current || ''); };
  const saveAbout = async (key) => {
    const value = aboutDraft.trim();
    try {
      await axios.put(`${API}/api/me/about`, { [key]: value }, { headers: authHeaders() });
      setData((d) => ({ ...d, about: { ...(d.about || {}), [key]: value || null } }));
      setEditingAbout(null);
    } catch { toast.error('Could not save'); }
  };

  const transcribe = async (v) => {
    const vid = v.video_id || v.url;
    if (transcripts[vid]) { setTranscriptView({ label: fmtDate(v.create_time), text: transcripts[vid] }); return; }
    setTranscribingId(vid);
    try {
      const r = await axios.post(`${API}/api/me/transcribe`, { url: v.url, video_id: v.video_id }, { headers: authHeaders(), timeout: 60000 });
      setTranscripts((t) => ({ ...t, [vid]: r.data.transcript }));
      setTranscriptView({ label: fmtDate(v.create_time), text: r.data.transcript });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transcription failed');
    } finally { setTranscribingId(null); }
  };

  // ---- No handle yet: show live product data + a sample of personal stats
  //       behind an "Add your TikTok handle" overlay (never a blank page). ----
  if (data?.needs_handle && !justAdded) {
    return (
      <AppShell title="Dashboard">
        <SEO title="Your creator dashboard" description="Track your TikTok performance." noindex />
        <div className="px-4 sm:px-8 py-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
              <TrendingUp className="w-6 h-6" strokeWidth={1.8} />
            </div>
            <div>
              <p className="font-display text-lg text-[#1F1B16]">Your creator dashboard</p>
              <p className="text-xs text-[#6F675D]">Browse what's selling right now — paste your TikTok profile to unlock your own stats.</p>
            </div>
          </div>
          <TrendingBlock />
          <DemoStats onAdd={() => setHandleOpen(true)} />
        </div>
        <HandleDialog open={handleOpen} onOpenChange={setHandleOpen} isFirstTime initial="" onSaved={onHandleSaved} />
      </AppShell>
    );
  }

  // ---- Preparing / loading ----
  if (loading || justAdded || (fetching && !data?.videos)) {
    return <PreparingView immediate={justAdded} fetching={fetching} onCheck={loadDashboard} />;
  }

  // ---- Dashboard ----
  return (
    <AppShell title="Dashboard">
      <SEO title="Your creator dashboard" description="Track your TikTok performance." noindex />
      <div className="px-4 sm:px-8 py-6">
        {/* Profile header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {creator?.avatar ? (
              <img src={proxyImg(creator.avatar)} alt="" referrerPolicy="no-referrer" className="w-12 h-12 rounded-full object-cover border border-[#E6DED1]" data-testid="creator-avatar" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center font-display text-lg" data-testid="creator-avatar">
                {(data?.handle || 'P').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <p className="font-display text-lg text-[#1F1B16]" data-testid="creator-handle">@{data?.handle}</p>
                <button onClick={() => setHandleOpen(true)} className="text-[#9D8E7B] hover:text-[#9D174D] transition-colors" title="Change handle" data-testid="edit-handle-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-xs text-[#6F675D]">
                {creator?.followers != null ? `${fmt(creator.followers)} followers · ` : ''}
                <span className="text-[#9D174D] font-medium">{data?.plan} plan</span> · up to {data?.video_cap} videos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data?.fetched_at && (
              <span className="text-xs text-[#9D8E7B]" data-testid="updated-at">
                Updated {new Date(data.fetched_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button
              variant="outline" size="sm" onClick={doRefresh} disabled={refreshing || !canRefresh}
              className="border-[#E6DED1] bg-[#FBF7F0] text-[#1F1B16] hover:bg-[#F0E8DB]"
              title={canRefresh ? 'Refresh now' : 'You can refresh once every 24 hours'}
              data-testid="refresh-btn"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Range presets */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="range-presets">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setActiveDays(p.days)}
              className={`h-9 px-4 rounded-full text-sm transition-colors border ${
                activeDays === p.days
                  ? 'bg-[#1F1B16] text-[#FFFDF9] border-[#1F1B16]'
                  : 'bg-[#FBF7F0] text-[#5E574E] border-[#E6DED1] hover:bg-[#F0E8DB]'
              }`}
              data-testid={`range-${p.days}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="totals">
          <StatCard icon={Film} label="Videos posted" value={videos.length} accent="text-[#9D174D]" />
          <StatCard icon={Eye} label="Total views" value={totals.views} accent="text-[#B45309]" />
          <StatCard icon={Heart} label="Total likes" value={totals.likes} accent="text-[#9D174D]" />
          <StatCard icon={MessageCircle} label="Total comments" value={totals.comments} accent="text-[#A3762F]" />
          <StatCard icon={Share2} label="Total shares" value={totals.shares} accent="text-[#1E7A6B]" />
        </div>

        {/* Earnings summary */}
        <div className="relative mb-6" data-testid="gmv-summary-wrap">
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${!gmvSummary.any ? 'blur-[2px] select-none pointer-events-none' : ''}`} data-testid="gmv-summary">
            <div className="rounded-[16px] border border-[#D7EDE7] bg-[#EEF7F4] p-4">
              <p className="text-[11px] uppercase tracking-wider text-[#5E8F83] mb-1">Total GMV</p>
              <p className="text-2xl font-display text-[#1E7A6B]">£{gmvSummary.gmv.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4">
              <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">Commission earned</p>
              <p className="text-2xl font-display text-[#1F1B16]">£{gmvSummary.commission.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4">
              <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-1">GMV per 1K views</p>
              <p className="text-2xl font-display text-[#1F1B16]">£{gmvSummary.per1k.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</p>
            </div>
          </div>
          {!gmvSummary.any && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center rounded-[16px] bg-[#FBF7F0]/70 backdrop-blur-[1px] px-4" data-testid="gmv-empty-overlay">
              <PoundSterling className="w-6 h-6 text-[#1E7A6B] mb-1.5" />
              <p className="text-sm font-display text-[#1F1B16]">No GMV added yet</p>
              <p className="text-xs text-[#9D8E7B] mb-3 max-w-xs">Tap a £ under any day in the chart below to log that day's GMV and commission.</p>
              {daily.length > 0 && (
                <button
                  onClick={nudgeGmv}
                  className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-sm font-semibold bg-[#1E7A6B] text-white hover:bg-[#19675a] transition-colors"
                  data-testid="add-gmv-link"
                >
                  <PoundSterling className="w-3.5 h-3.5" /> Show me how
                </button>
              )}
            </div>
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-3 mb-6" data-testid="charts" ref={gmvChartRef}>
          <ChartCard
            title="Videos posted per day"
            data={dailyWithGmv}
            dataKey="videos"
            color="#9D174D"
            overlayKey="gmv"
            overlayColor="#1E7A6B"
            overlayName="GMV"
            footer={(
              <div className="relative">
                {gmvHint && (
                  <div className="absolute -top-9 left-1/2 -translate-x-1/2 z-10 bg-[#1F1B16] text-[#FFFDF9] text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-bounce" data-testid="gmv-hint-tooltip">
                    Tap a £ below to add GMV for that day
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1F1B16] rotate-45" />
                  </div>
                )}
                <div className="flex items-center justify-between mt-2" style={{ paddingLeft: 28, paddingRight: 6 }} data-testid="gmv-day-buttons">
                  {daily.map((d) => {
                    const done = gmvByDay[d.key] != null;
                    return (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => openGmvForDay(d.key)}
                        title={`Add GMV for ${d.label}`}
                        className={`inline-flex items-center justify-center w-5 h-5 rounded-md border transition-colors ${
                          done ? 'bg-[#D7EDE7] border-[#1E7A6B] text-[#1E7A6B]' : 'border-[#E6DED1] text-[#B9AE9D] hover:text-[#1E7A6B] hover:border-[#1E7A6B]/60'
                        } ${gmvHint && !done ? 'ring-2 ring-[#1E7A6B]/50 animate-pulse' : ''}`}
                        data-testid={`gmv-day-btn-${d.key}`}
                      >
                        <PoundSterling className="w-3 h-3" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          />
          <ChartCard title="Views per day" data={daily} dataKey="views" color="#B45309" />
        </div>
        <p className="text-xs text-[#9D8E7B] mb-8 -mt-3">Tap a £ under a day to log that day's GMV and commission earned — it's saved to your account.</p>

        {/* Recent videos */}
        <div className="mt-10" data-testid="recent-videos-section">
        {videos.length === 0 ? (
          <p className="text-sm text-[#6F675D]" data-testid="no-videos">No videos posted in this period.</p>
        ) : (
          <>
            <ProductsInVideos videos={videos} />
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="font-display text-base text-[#1F1B16]" data-testid="videos-heading">Recent videos</h3>
              <button
                type="button"
                onClick={() => { setAdsOnly((s) => !s); setShowAllVideos(false); }}
                disabled={adCount === 0}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  adsOnly
                    ? 'bg-[#9D174D] text-white border-[#9D174D]'
                    : 'bg-[#FBF7F0] text-[#5E574E] border-[#E6DED1] hover:bg-[#F0E8DB]'
                }`}
                data-testid="videos-ads-filter"
                title={adCount === 0 ? 'No ads in this period' : 'Show only ads / paid partnerships'}
              >
                <span className="text-[9px] font-bold tracking-wide bg-current/0 rounded px-1 py-0.5" style={{ border: adsOnly ? '1px solid rgba(255,255,255,0.7)' : '1px solid #9D174D', color: adsOnly ? '#fff' : '#9D174D' }}>AD</span>
                Ads only{adCount > 0 ? ` (${adCount})` : ''}
              </button>
            </div>
            {displayVideos.length === 0 ? (
              <p className="text-sm text-[#6F675D]" data-testid="no-ads">No ads or paid partnerships in this period.</p>
            ) : (
            <div className="grid grid-cols-3 lg:grid-cols-8 gap-2" data-testid="video-grid">
              {(showAllVideos ? displayVideos : displayVideos.slice(0, 8)).map((v, i) => {
                const vid = v.video_id || v.url;
                const busy = transcribingId === vid;
                const hasScript = !!transcripts[vid];
                return (
                  <div
                    key={vid || i}
                    className={`rounded-[10px] border border-[#EFE1D1] bg-[#FBF7F0] overflow-hidden ${!showAllVideos && i >= 6 ? 'hidden lg:block' : ''}`}
                    data-testid={`video-${i}`}
                  >
                    <a href={v.url || '#'} target="_blank" rel="noopener noreferrer" className="block relative group">
                      {v.cover ? (
                        <img src={proxyImg(v.cover)} alt="" referrerPolicy="no-referrer" className="w-full aspect-[9/16] object-cover" />
                      ) : (
                        <div className="w-full aspect-[9/16] bg-[#F0E8DB] flex items-center justify-center">
                          <Film className="w-4 h-4 text-[#C9BCA8]" />
                        </div>
                      )}
                      <span className="absolute top-1 right-1 bg-black/55 text-white rounded px-1 py-0.5 inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ExternalLink className="w-2.5 h-2.5" />
                      </span>
                      {v.is_ad && (
                        <span className="absolute top-1 left-1 bg-[#9D174D] text-white text-[9px] font-bold tracking-wide rounded px-1.5 py-0.5 shadow-sm" title="Ad or paid partnership" data-testid={`video-ad-badge-${i}`}>
                          AD
                        </span>
                      )}
                      {v.product_link && <ShopBadge offset={v.is_ad} testid={`video-shop-badge-${i}`} />}
                    </a>
                    <div className="px-2 py-1.5">
                      <p className="text-[10px] text-[#9D8E7B] mb-1">{fmtDate(v.create_time)}</p>
                      <div className="flex items-center justify-between text-[10px] font-medium text-[#3C362D]">
                        <span className="inline-flex items-center gap-0.5" title="Views"><Eye className="w-3 h-3 text-[#B45309]" strokeWidth={2} />{fmt(v.views)}</span>
                        <span className="inline-flex items-center gap-0.5" title="Likes"><Heart className="w-3 h-3 text-[#9D174D]" strokeWidth={2} />{fmt(v.likes)}</span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-medium text-[#3C362D] mt-0.5">
                        <span className="inline-flex items-center gap-0.5" title="Comments"><MessageCircle className="w-3 h-3 text-[#A3762F]" strokeWidth={2} />{fmt(v.comments)}</span>
                        <span className="inline-flex items-center gap-0.5" title="Shares"><Share2 className="w-3 h-3 text-[#1E7A6B]" strokeWidth={2} />{fmt(v.shares)}</span>
                      </div>
                      {v.product_link && <ViewProductLink link={v.product_link} testid={`video-view-product-${i}`} />}
                      <button
                        type="button"
                        onClick={() => transcribe(v)}
                        disabled={busy}
                        className="mt-1.5 w-full inline-flex items-center justify-center gap-1 h-6 rounded-md border border-[#E6DED1] text-[9px] font-semibold text-[#9D174D] hover:bg-[#FBE4EE] disabled:opacity-60 transition-colors"
                        data-testid={`transcribe-${i}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                        {busy ? 'Working…' : (hasScript ? 'View script' : 'Transcribe')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            {displayVideos.length > 8 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowAllVideos((s) => !s)}
                  className="text-sm font-medium text-[#9D174D] hover:underline"
                  data-testid="show-more-videos"
                >
                  {showAllVideos ? 'Show fewer videos' : `Show more videos (${displayVideos.length - 8} more)`}
                </button>
              </div>
            )}
          </>
        )}
        </div>

        {/* About you */}
        <div className="mt-10" data-testid="about-section">
          <h3 className="font-display text-base text-[#1F1B16] mb-1">About you</h3>
          <p className="text-xs text-[#9D8E7B] mb-3">Tap any item to edit — this helps tailor your scripts.</p>
          <div className="rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] divide-y divide-[#EFE1D1]">
            {[['gender', 'Gender'], ['marital_status', 'Marital status'], ['kids', 'Kids'], ['pets', 'Pets']].map(([key, label]) => {
              const val = data?.about?.[key];
              const editing = editingAbout === key;
              return (
                <div key={key} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`about-row-${key}`}>
                  <span className="text-sm text-[#8A8178] w-28 sm:w-32 shrink-0">{label}</span>
                  {editing ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input value={aboutDraft} onChange={(e) => setAboutDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveAbout(key); }} autoFocus className="h-8 bg-[#FFFDF9] border-[#E6DED1] text-[#1F1B16]" data-testid={`about-input-${key}`} />
                      <button onClick={() => saveAbout(key)} className="text-[#1E7A6B] shrink-0" data-testid={`about-save-${key}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingAbout(null)} className="text-[#9D8E7B] shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => startEditAbout(key, val)} className="flex-1 text-left text-sm text-[#1F1B16] hover:text-[#9D174D] inline-flex items-center justify-between group" data-testid={`about-value-${key}`}>
                      <span>{val ? val : <span className="text-[#C2B6A5]">Add {label.toLowerCase()}…</span>}</span>
                      <Pencil className="w-3 h-3 text-[#C2B6A5] group-hover:text-[#9D174D]" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 10 Products — below the creator's own stats */}
        <TrendingBlock />
      </div>

      {/* Transcript modal */}
      <Dialog open={!!transcriptView} onOpenChange={(o) => { if (!o) setTranscriptView(null); }}>
        <DialogContent className="bg-[#FFFDF9] border-[#E6DED1] sm:max-w-lg" data-testid="transcript-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1F1B16]">Video transcript{transcriptView?.label ? ` — ${transcriptView.label}` : ''}</DialogTitle>
            <DialogDescription className="text-[#6F675D]">Pulled from this TikTok. Copy it into Create Script to reuse the structure.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto rounded-[12px] bg-[#FBF7F0] border border-[#EFE1D1] p-3 text-sm text-[#3C362D] whitespace-pre-wrap" data-testid="transcript-text">{transcriptView?.text}</div>
          <Button onClick={() => { navigator.clipboard.writeText(transcriptView?.text || ''); toast.success('Transcript copied'); }} className="bg-[#1F1B16] text-[#FFFDF9] hover:bg-[#332d24]" data-testid="transcript-copy">Copy transcript</Button>
        </DialogContent>
      </Dialog>

      {/* GMV dialog */}
      <Dialog open={gmvOpen} onOpenChange={setGmvOpen}>
        <DialogContent className="bg-[#FFFDF9] border-[#E6DED1] sm:max-w-md" data-testid="gmv-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1F1B16]">Record GMV — {dayLabel(gmvForm.day)}</DialogTitle>
            <DialogDescription className="text-[#6F675D]">
              Enter this day's GMV total and the commission you earned (both actual £ amounts). Saved to your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6F675D]">GMV total (£)</Label>
                <Input type="number" min="0" value={gmvForm.gmv} onChange={(e) => setGmvForm((f) => ({ ...f, gmv: e.target.value }))} placeholder="0" className="bg-[#FBF7F0] border-[#E6DED1] text-[#1F1B16] mt-1" data-testid="gmv-amount-input" />
              </div>
              <div>
                <Label className="text-xs text-[#6F675D]">Commission earned (£)</Label>
                <Input type="number" min="0" value={gmvForm.commission} onChange={(e) => setGmvForm((f) => ({ ...f, commission: e.target.value }))} placeholder="0" className="bg-[#FBF7F0] border-[#E6DED1] text-[#1F1B16] mt-1" data-testid="gmv-commission-input" />
              </div>
            </div>
            <div className="flex gap-2">
              {gmvByDay[gmvForm.day] != null && (
                <Button variant="outline" onClick={() => removeGmvEntry(gmvForm.day)} className="border-[#E6DED1] bg-transparent text-[#5E574E] hover:bg-[#F0E8DB]" data-testid="gmv-remove-btn">Remove</Button>
              )}
              <Button onClick={saveGmvEntry} className="flex-1 bg-[#1E7A6B] hover:bg-[#19675a] text-white" data-testid="gmv-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <HandleDialog open={handleOpen} onOpenChange={setHandleOpen} isFirstTime={false} initial={data?.handle} onSaved={onHandleSaved} />
    </AppShell>
  );
};

export default CreatorDashboardPage;
